import i18n from 'i18next';
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { authLogin, authLogout, authMe, authStatus } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount and periodically (keep-alive)
  useEffect(() => {
    let mounted = true;
    let intervalId = null;

    // Full session check - gets user data
    async function checkSession() {
      try {
        const userData = await authMe();
        if (mounted) {
          setUser(userData);
          if (userData.language) {
            i18n.changeLanguage(userData.language);
          }
        }
      } catch (e) {
        // No valid session, clear user (triggers redirect to login)
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    // Lightweight keep-alive check
    async function checkStatus() {
      try {
        await authStatus();
        // Session still valid, no action needed
      } catch (e) {
        // Session expired, clear user (triggers redirect to login)
        if (mounted) {
          setUser(null);
        }
      }
    }

    // Initial check (full user data)
    checkSession();

    // Periodic keep-alive every 60 seconds (lightweight status check)
    intervalId = setInterval(() => {
      if (mounted) {
        checkStatus();
      }
    }, 60000);

    return () => {
      mounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  const value = useMemo(() => {
    const isAuthenticated = !!user;

    const login = async (username, password) => {
      try {
        const userData = await authLogin(username, password);
        setUser(userData);
        if (userData.language) {
          i18n.changeLanguage(userData.language);
        }
        return { ok: true };
      } catch (e) {
        const message =
          e.response?.data?.detail ||
          e.response?.data?.message ||
          e.message ||
          "Login failed";
        return { ok: false, message };
      }
    };

    const logout = async () => {
      try {
        await authLogout();
      } catch (e) {
        // Logout failed, but clear local state anyway
        console.warn("Logout API call failed:", e);
      }
      setUser(null);
    };

    return { user, isAuthenticated, login, logout, loading };
  }, [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
